import React, { useState, useEffect } from "react";

import {
  PageHero,
  ViewToggle,
  SectionTitle,
  SectionWithShapes,
  type ViewMode,
} from "../components/common";
import {
  FeaturedInitiative,
  FeaturedInitiativeSpotlight,
  InitiativeCard,
} from "../components/initiatives";
import {
  CTASection,
  LearningPackage,
  ParticipantsOverview,
  VoiceOfParticipants,
} from "../components/sections";
import { useInitiativePosts } from "../hooks/useInitiativePosts";
import { fetchAiLiteracyTutorBanner } from "../services/aiLiteracyTutorService";
import type { AiLiteracyTutorBanner } from "../types";

const VIEW_STORAGE_KEY = "initiatives-view";

type InitiativeCardData = {
  id?: number | string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  eng_slug: string | null;
  detailHref: string;
};

const Initiatives: React.FC = () => {
  const { initiatives, loading, error } = useInitiativePosts();
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [tutorBanner, setTutorBanner] =
    useState<AiLiteracyTutorBanner | null>(null);
  const [tutorLoading, setTutorLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY) as ViewMode | null;
    if (stored === "grid" || stored === "list") setViewMode(stored);
  }, []);

  useEffect(() => {
    fetchAiLiteracyTutorBanner()
      .then(setTutorBanner)
      .catch((err) => {
        console.error("Failed to load AI Literacy Tutor initiative", err);
      })
      .finally(() => setTutorLoading(false));
  }, []);

  const handleViewChange = (mode: ViewMode) => {
    setViewMode(mode);
    localStorage.setItem(VIEW_STORAGE_KEY, mode);
  };

  const initiativeCards: InitiativeCardData[] = initiatives.map((post) => ({
    id: post.id,
    title: post.eng_title,
    description: post.eng_caption,
    image: post.eng_thumbnail,
    imageAlt: `${post.eng_title} Image`,
    eng_slug: post.eng_slug,
    detailHref:
      post.eng_slug === "professor-e"
        ? "/professor-e"
        : `/initiatives/${post.id}`,
  }));

  const orderedInitiatives = [...initiativeCards];
  if (tutorBanner) {
    const requestedPosition = Math.max(
      1,
      tutorBanner.initiative_position || 1,
    );
    const insertionIndex = Math.min(
      requestedPosition - 1,
      orderedInitiatives.length,
    );
    orderedInitiatives.splice(insertionIndex, 0, {
      id: "ai-literacy-tutor",
      title: tutorBanner.title || "AI Literacy Tutor",
      description: tutorBanner.subtitle || "",
      image: tutorBanner.image,
      imageAlt: `${tutorBanner.title || "AI Literacy Tutor"} Image`,
      eng_slug: "ai-literacy-tutor",
      detailHref: "/ai-literacy-tutor",
    });
  }

  const featuredInitiative = orderedInitiatives[0] ?? null;
  const restInitiatives = orderedInitiatives.slice(1);

  return (
    <main id="main-content" role="main">
      <PageHero
        title="Our Initiatives"
        subtitle="Empowering education through innovative AI programs and collaborative projects"
      />

      {loading || tutorLoading ? (
        <SectionWithShapes>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading initiatives...</span>
            </div>
          </div>
        </SectionWithShapes>
      ) : error ? (
        <SectionWithShapes>
          <div className="text-center py-5">
            <p className="mb-0">
              Failed to load initiatives. Please try again later.
            </p>
          </div>
        </SectionWithShapes>
      ) : (
        <>
          {featuredInitiative && (
            <SectionWithShapes id="featured-programs">
              <SectionTitle
                subtitle="[ OUR INITIATIVES ]"
                title="Featured Programs"
                textCenter
              />
              <FeaturedInitiativeSpotlight
                title={featuredInitiative.title}
                description={featuredInitiative.description}
                image={featuredInitiative.image}
                imageAlt={featuredInitiative.imageAlt}
                detailHref={
                  featuredInitiative.detailHref
                }
              />
            </SectionWithShapes>
          )}

          {restInitiatives.length > 0 && (
            <SectionWithShapes id="initiatives">
              <div
                className="d-flex justify-content-end align-items-center mb-4 initiatives-view-bar"
                style={{ gap: "0.5rem" }}
              >
                <span
                  className="text-muted"
                  style={{ fontSize: "0.9rem", marginRight: "0.5rem" }}
                >
                  View
                </span>
                <ViewToggle
                  value={viewMode}
                  onChange={handleViewChange}
                  ariaLabel="Initiatives view: grid or list"
                />
              </div>
              {viewMode === "grid" ? (
                <div className="row">
                  {restInitiatives.map((initiative) => (
                    <div
                      key={initiative.id ?? initiative.title}
                      className="col-lg-6 mb-4"
                    >
                      <InitiativeCard
                        image={initiative.image}
                        imageAlt={initiative.imageAlt}
                        title={initiative.title}
                        description={initiative.description}
                        buttons={
                          [
                            {
                              text: "Know More",
                              href: initiative.detailHref,
                              variant: "line" as const,
                            },
                          ]
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : (
                restInitiatives.map((initiative) => (
                  <div key={initiative.id ?? initiative.title} className="mb-5">
                    <FeaturedInitiative
                      tagline="Initiative"
                      title={initiative.title}
                      description={initiative.description}
                      image={initiative.image}
                      imageAlt={initiative.imageAlt}
                      buttons={
                        [
                          {
                            text: "Know More",
                            href: initiative.detailHref,
                            variant: "line" as const,
                          },
                        ]
                      }
                    />
                  </div>
                ))
              )}
            </SectionWithShapes>
          )}
        </>
      )}

      <ParticipantsOverview />

      <VoiceOfParticipants />

      <LearningPackage />

      <CTASection
        title="Want to Get Involved?"
        description="Join us in shaping the future of AI literacy in education. Whether you're a student, educator, or industry partner, there's a place for you in our community."
        buttonText="Contact Us"
        buttonLink="/#contact-us"
        buttonIcon="fas fa-arrow-right"
      />
    </main>
  );
};

export default Initiatives;
